// Browser-compatible storage service
// Uses localStorage in browser, file storage on server

const isBrowser = typeof window !== "undefined";

// Cache invalidation and TTL configuration
const DEFAULT_TTL = 15 * 60 * 1000; // 15 minutes default TTL

// Define which cache keys should invalidate other related keys
// When one key is updated, these related keys are automatically cleared
const CACHE_INVALIDATION_MAP: Record<string, string[]> = {
  auth_user: ["weeklyReports", "auth_*"], // User change invalidates reports and auth data
  jwt_token: ["auth_user", "weeklyReports"], // Token change invalidates user and reports
  auth_users: ["auth_user"], // User list change invalidates current user
  weeklyReports: [], // Reports don't invalidate other caches
  pending_role_changes: ["auth_users"], // Role changes invalidate user list
};

// Browser storage implementation with TTL and cache invalidation
const browserStorage = {
  getItem: (key: string): string | null => {
    try {
      const storedValue = localStorage.getItem(key);
      if (!storedValue) return null;

      // Check if value has TTL metadata
      try {
        const parsed = JSON.parse(storedValue);
        if (parsed.value && parsed.timestamp && parsed.ttl) {
          // Check if cache has expired
          const age = Date.now() - parsed.timestamp;
          if (age > parsed.ttl) {
            console.log(`Cache expired for ${key}, removing...`);
            localStorage.removeItem(key);
            return null;
          }
          return parsed.value;
        }
      } catch {
        // Not TTL-formatted, return as-is for backward compatibility
      }

      return storedValue;
    } catch (error) {
      console.error(`Error reading ${key} from localStorage:`, error);
      return null;
    }
  },

  setItem: (key: string, value: string, ttl?: number): void => {
    try {
      // Invalidate related caches
      browserStorage.invalidateRelatedCaches(key);

      // Store with TTL metadata if TTL is specified
      const cacheData = ttl
        ? {
            value,
            timestamp: Date.now(),
            ttl: ttl,
          }
        : value;

      const storageValue =
        typeof cacheData === "string" ? cacheData : JSON.stringify(cacheData);
      localStorage.setItem(key, storageValue);
    } catch (error) {
      console.error(`Error saving ${key} to localStorage:`, error);
    }
  },

  removeItem: (key: string): void => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Error removing ${key} from localStorage:`, error);
    }
  },

  clear: (): void => {
    try {
      localStorage.clear();
    } catch (error) {
      console.error("Error clearing localStorage:", error);
    }
  },

  key: (index: number): string | null => {
    try {
      return localStorage.key(index);
    } catch (error) {
      console.error("Error getting key at index:", error);
      return null;
    }
  },

  get length(): number {
    try {
      return localStorage.length;
    } catch (error) {
      console.error("Error getting localStorage length:", error);
      return 0;
    }
  },

  // Cache invalidation helper
  invalidateRelatedCaches: (key: string): void => {
    const invalidationPatterns = CACHE_INVALIDATION_MAP[key] || [];

    invalidationPatterns.forEach((pattern) => {
      if (pattern.includes("*")) {
        // Handle wildcard patterns
        const prefix = pattern.replace("*", "");
        Object.keys(localStorage).forEach((storageKey) => {
          if (storageKey.startsWith(prefix)) {
            console.log(`Invalidating related cache: ${storageKey}`);
            localStorage.removeItem(storageKey);
          }
        });
      } else if (localStorage.getItem(pattern)) {
        console.log(`Invalidating related cache: ${pattern}`);
        localStorage.removeItem(pattern);
      }
    });
  },

  // Clean expired cache entries
  cleanExpiredCache: (): void => {
    try {
      const keysToRemove: string[] = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) {
          const value = localStorage.getItem(key);
          if (value) {
            try {
              const parsed = JSON.parse(value);
              if (parsed.timestamp && parsed.ttl) {
                const age = Date.now() - parsed.timestamp;
                if (age > parsed.ttl) {
                  keysToRemove.push(key);
                }
              }
            } catch {
              // Not TTL-formatted, skip
            }
          }
        }
      }

      keysToRemove.forEach((key) => {
        console.log(`Removing expired cache: ${key}`);
        localStorage.removeItem(key);
      });
    } catch (error) {
      console.error("Error cleaning expired cache:", error);
    }
  },
};

// Server storage implementation (only used when not in browser)
let serverStorage: any = null;

if (!isBrowser) {
  // Dynamic import for server-side modules
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const fs = require("fs");
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const path = require("path");

  // Storage configuration
  const STORAGE_PATH = process.env.STORAGE_PATH || "./data";
  const USER_DATA_DIR = path.join(STORAGE_PATH, "users");
  const REPORTS_DIR = path.join(STORAGE_PATH, "reports");
  const TEMPLATES_DIR = path.join(STORAGE_PATH, "templates");
  const AUTH_DIR = path.join(STORAGE_PATH, "auth");
  const SETTINGS_DIR = path.join(STORAGE_PATH, "settings");

  // Ensure all required directories exist
  const ensureDirectories = () => {
    [
      STORAGE_PATH,
      USER_DATA_DIR,
      REPORTS_DIR,
      TEMPLATES_DIR,
      AUTH_DIR,
      SETTINGS_DIR,
    ].forEach((dir) => {
      if (!fs.existsSync(dir)) {
        fs.mkdirSync(dir, { recursive: true });
        console.log(`Created directory: ${dir}`);
      }
    });
  };

  // Initialize directories
  ensureDirectories();

  // Cache to track original values and avoid duplicate writes
  const valueCache = new Map<string, string>();

  // Function to get filename for a specific key
  const getFilenameForKey = (key: string): string => {
    // Map keys to their respective directories
    const keyMap: Record<string, string> = {
      // Auth related
      auth_user: path.join(AUTH_DIR, "current-user.json"),
      auth_users: path.join(AUTH_DIR, "users.json"),
      auth_credentials: path.join(AUTH_DIR, "credentials.json"),
      token: path.join(AUTH_DIR, "token.json"),
      gmail_access_token: path.join(AUTH_DIR, "gmail-access-token.json"),
      gmail_refresh_token: path.join(AUTH_DIR, "gmail-refresh-token.json"),
      gmail_token_expiry: path.join(AUTH_DIR, "gmail-token-expiry.json"),
      pending_role_changes: path.join(AUTH_DIR, "pending-role-changes.json"),

      // Reports related
      weeklyReports: path.join(REPORTS_DIR, "reports.json"),
      report_date_range: path.join(REPORTS_DIR, "date-range.json"),
      report_date_objects: path.join(REPORTS_DIR, "date-objects.json"),
      from_compliance_tab: path.join(REPORTS_DIR, "compliance-tab.json"),

      // Settings related
      theme: path.join(SETTINGS_DIR, "theme.json"),
      dashboard_active_tab: path.join(SETTINGS_DIR, "dashboard-tab.json"),
    };

    return keyMap[key] || path.join(STORAGE_PATH, `${key}.json`);
  };

  serverStorage = {
    getItem: (key: string): string | null => {
      try {
        const filePath = getFilenameForKey(key);
        if (fs.existsSync(filePath)) {
          return fs.readFileSync(filePath, "utf8");
        }
        return null;
      } catch (error) {
        console.error(`Error reading ${key} from storage:`, error);
        return null;
      }
    },

    setItem: (key: string, value: string): void => {
      try {
        // Skip if value hasn't changed
        if (valueCache.get(key) === value) return;
        valueCache.set(key, value);

        const filePath = getFilenameForKey(key);
        fs.writeFileSync(filePath, value, "utf8");
        console.log(`Saved ${key} to storage at ${filePath}`);
      } catch (error) {
        console.error(`Error saving ${key} to storage:`, error);
      }
    },

    removeItem: (key: string): void => {
      try {
        const filePath = getFilenameForKey(key);
        if (fs.existsSync(filePath)) {
          fs.unlinkSync(filePath);
          console.log(`Removed ${key} from storage`);
        }
        valueCache.delete(key);
      } catch (error) {
        console.error(`Error removing ${key} from storage:`, error);
      }
    },

    clear: (): void => {
      try {
        // Clear all files in each directory
        [AUTH_DIR, REPORTS_DIR, SETTINGS_DIR].forEach((dir) => {
          if (fs.existsSync(dir)) {
            fs.readdirSync(dir).forEach((file) => {
              fs.unlinkSync(path.join(dir, file));
            });
          }
        });
        valueCache.clear();
        console.log("Cleared all storage");
      } catch (error) {
        console.error("Error clearing storage:", error);
      }
    },

    key: (index: number): string | null => {
      try {
        const allKeys: string[] = [];
        [AUTH_DIR, REPORTS_DIR, SETTINGS_DIR].forEach((dir) => {
          if (fs.existsSync(dir)) {
            fs.readdirSync(dir).forEach((file) => {
              allKeys.push(file.replace(".json", ""));
            });
          }
        });
        return allKeys[index] || null;
      } catch (error) {
        console.error("Error getting key at index:", error);
        return null;
      }
    },

    get length(): number {
      try {
        let count = 0;
        [AUTH_DIR, REPORTS_DIR, SETTINGS_DIR].forEach((dir) => {
          if (fs.existsSync(dir)) {
            count += fs.readdirSync(dir).length;
          }
        });
        return count;
      } catch (error) {
        console.error("Error getting storage length:", error);
        return 0;
      }
    },
  };
}

// Clean expired cache entries on module load (client-side only)
if (isBrowser && typeof window !== "undefined") {
  // Clean expired cache when the module loads
  browserStorage.cleanExpiredCache();

  // Clean expired cache periodically (every 5 minutes)
  setInterval(
    () => {
      browserStorage.cleanExpiredCache();
    },
    5 * 60 * 1000,
  );
}

// Export the appropriate storage implementation
export const storage = isBrowser ? browserStorage : serverStorage;
